import { Map, Marker, NavigationControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef } from "react";
import { OSMStyle } from "utils/data/map.ts";
import markerPng from "assets/images/marker.png";

export type LatLng = { lat: number, lng: number };

const CIRCLE_SOURCE_ID = "around-circle";

//build a GeoJSON polygon approximating a geographic circle of the given radius (km)
function circleFeature(center: LatLng, radiusKm: number, points = 72) {
  const coords: [number, number][] = [];
  const distanceX = radiusKm / (111.320 * Math.cos((center.lat * Math.PI) / 180));
  const distanceY = radiusKm / 110.574;
  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    coords.push([center.lng + distanceX * Math.cos(theta), center.lat + distanceY * Math.sin(theta)]);
  }
  coords.push(coords[0]);
  return {
    type: "Feature" as const,
    geometry: { type: "Polygon" as const, coordinates: [coords] },
    properties: {}
  };
}

//diameter (km) the circle should represent; the backend filters on the radius
function AroundMapPicker(props: {
  center: LatLng | null,
  diameterKm: number,
  onCenterChange: (center: LatLng) => void
}) {
  const mapRef = useRef<Map | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const loadedRef = useRef<boolean>(false);
  //keep the latest callback so the map click handler never goes stale
  const onCenterChangeRef = useRef(props.onCenterChange);
  onCenterChangeRef.current = props.onCenterChange;

  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) {
      return;
    }

    const map = new Map({
      container: mapDivRef.current,
      attributionControl: false,
      dragRotate: false,
      touchZoomRotate: true,
      maxZoom: 10,
      minZoom: 4,
      //@ts-expect-error style is not in the types
      style: OSMStyle,
      center: props.center ? { lat: props.center.lat, lng: props.center.lng } : { lat: 46, lng: -73 },
      zoom: props.center ? 6 : 5
    });
    mapRef.current = map;

    //zoom in/out buttons; compass is hidden since rotation is disabled
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      loadedRef.current = true;
      map.addSource(CIRCLE_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] }
      });
      map.addLayer({
        id: `${CIRCLE_SOURCE_ID}-fill`,
        type: "fill",
        source: CIRCLE_SOURCE_ID,
        paint: { "fill-color": "#22c55e", "fill-opacity": 0.15 }
      });
      map.addLayer({
        id: `${CIRCLE_SOURCE_ID}-line`,
        type: "line",
        source: CIRCLE_SOURCE_ID,
        paint: { "line-color": "#22c55e", "line-width": 2 }
      });
      //draw the initial state once the source exists
      updateOverlay();
    });

    map.on("click", (e) => {
      onCenterChangeRef.current({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      loadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //redraw the marker + circle whenever the center or diameter changes
  const updateOverlay = () => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const center = props.center;
    if (!center) {
      markerRef.current?.remove();
      markerRef.current = null;
      if (loadedRef.current && map.getSource(CIRCLE_SOURCE_ID)) {
        (map.getSource(CIRCLE_SOURCE_ID) as any).setData({ type: "FeatureCollection", features: [] });
      }
      return;
    }

    if (!markerRef.current) {
      const markerElement = document.createElement("div");
      markerElement.innerHTML = `<img class="block min-w-[40px] w-[40px] h-[40px]" src="${markerPng}" alt="marker"/>`;
      markerRef.current = new Marker({ element: markerElement }).setLngLat([center.lng, center.lat]).addTo(map);
    } else {
      markerRef.current.setLngLat([center.lng, center.lat]);
    }

    if (loadedRef.current && map.getSource(CIRCLE_SOURCE_ID)) {
      (map.getSource(CIRCLE_SOURCE_ID) as any).setData({
        type: "FeatureCollection",
        features: [circleFeature(center, props.diameterKm / 2)]
      });
    }
  };

  useEffect(() => {
    updateOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.center, props.diameterKm]);

  //recenter the map when an external source (e.g. selecting an event) moves the center
  useEffect(() => {
    if (mapRef.current && props.center) {
      mapRef.current.easeTo({ center: [props.center.lng, props.center.lat], zoom: 6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.center?.lat, props.center?.lng]);

  return <div ref={mapDivRef} className="h-72 w-full overflow-hidden rounded-md border border-gray-200" />;
}

export default AroundMapPicker;
